/** Strings of `/users`, `/users/:id`, the user components and the rates panel: `usersEn` is the source, `usersCs` must have the same keys. */
export const usersEn = {
  usr: {
    list: {
      title: "Users",
      total_one: "{{count}} user total",
      total_other: "{{count}} users total",
      total_few: "{{count}} users total",
      search: "Search by name or email...",
      empty: "No users found.",
      showFormer: "Show former too",
      former: "former",
      formerSuffix: "(former)",
      viewAsTitle: "View the Hub as this user (read-only)",
      viewAs: "View as",
      columns: {
        name: "Name",
        email: "Email",
        role: "Role",
        gitlabId: "GitLab ID",
        iban: "IBAN",
        entries: "Entries",
        joined: "Joined",
        actions: "Actions",
      },
    },
    roleBadge: {
      admin: "admin",
      accountant: "accountant",
    },
    role: {
      admin: "Admin",
      accountant: "Accountant",
    },
    approval: {
      worked: "worked",
      approved: "approved",
      paid: "paid",
      rejected: "rejected",
    },
    billing: {
      unbilled: "unbilled",
      to_invoice: "to_invoice",
      invoiced: "invoiced",
      non_billable: "non_billable",
    },
    detail: {
      clockify: "Clockify: {{name}}",
      noProject: "No project",
      overhead: "overhead",
      backToUsers: "Back to Users",
      globalRole: "Global Role",
      discord: "Discord",
      linked: "linked",
      notLinked: "not linked",
      editUser: "Edit User",
      editDescription: "Update global role and payment information. Created <date/>.",
      meetingTitle: "Meeting identity",
      meetingDescription:
        "Whether a recorded meeting can tell this participant is this person — and so whether it proposes their time at all. Discord matches on the account id the bot resolves from the handle; Google Meet matches on email and nothing else, so a Meet account on a different address than the Hub sign-in has to be claimed here. The user can maintain both themselves from their own profile.",
      timeEntries: "Time Entries",
      timeEntriesCount: "{{count}} entries (showing latest 50)",
      timeEntriesEmpty: "No time entries yet.",
      costRates: "Cost rates",
      costRatesDescription:
        'What this user\'s hour costs the company. Effective-dated — a raise is a new row with a later "Valid from", not an edit. Used for margin reports; not user-visible.',
      columns: {
        project: "Project",
        workItem: "Work Item",
        hours: "Hours",
        date: "Date",
        approval: "Approval",
        billing: "Billing",
      },
    },
    signIn: {
      sent: "Sign-in link sent",
      notSent: "The sign-in link wasn't sent",
      failed: "Couldn't send the link",
      send: "Send sign-in link",
    },
    offboard: {
      title: "Removed from {{projects}} projects, {{items}} work items reassigned",
      notRemoved: "Not removed: {{list}}",
      manager: "You are now the manager of: {{list}}",
      signedOut: "Signed out of {{sessions}} sessions, {{tokens}} API tokens revoked",
      failed: "Offboarding failed",
      confirm:
        "Remove {{name}} from every project (GitLab too), hand their open work items to the project defaults, sign them out and disable their Hub account?",
      removing: "Removing",
      button: "Remove from all projects",
    },
    disableAccess: {
      button: "Disable Hub access",
      confirm:
        "Disable Hub access for {{name}}? They are signed out and can't sign in again. Their projects and GitLab membership stay as they are.",
      disabling: "Disabling",
      done: "Hub access disabled. Projects and GitLab membership unchanged.",
      failed: "Couldn't disable Hub access",
    },
    disabled: {
      badge: "Disabled",
      enable: "Enable account",
      enabling: "Enabling",
      enabled: "Account enabled. Add their projects and roles again by hand.",
      failed: "Couldn't enable the account",
    },
    edit: {
      updated: "User updated",
      error: "Error",
      updateFailed: "Update failed",
      globalRole: "Global Role",
      noneRole: "None (project member only)",
      ibanPlaceholder: "e.g. CZ6508000000192000145399",
      saving: "Saving",
      save: "Save Changes",
    },
    newClient: {
      created: "Client account created",
      noInvite: "Add them to a project on its Members page.",
      invited: "The invitation is on its way. Add them to a project on its Members page.",
      inviteFailed:
        "The account exists, but the invitation wasn't sent. {{error}} Resend it from the user's page once e-mail works.",
      trigger: "New client",
      title: "New client account",
      intro:
        "For a client without GitLab. They sign in with a link sent to this e-mail and see only the projects where they have the Client role.",
      name: "Name",
      email: "E-mail",
      sendInvite: "Send an invitation now (the link works for 7 days)",
      cancel: "Cancel",
      create: "Create",
    },
    discord: {
      saveFailed: "Couldn't save the handle.",
      handle: "Discord handle",
      placeholder: "e.g. vita.mergl",
      linkedState: "Account linked — meetings can propose time for this person.",
      notLinkedState: "Not linked — no time proposed from Discord meetings.",
      help: "Saved, then resolved to a real Discord account by the bot. Only the resolved account — not the handle itself — lets a recorded meeting propose your time.",
      linking: "Linking",
      save: "Save handle",
    },
    emails: {
      loadFailed: "Couldn't load the addresses.",
      addFailed: "Couldn't add that address.",
      removeFailed: "Couldn't remove that address.",
      signIn: "sign-in",
      remove: "Remove",
      add: "Add an address",
      placeholder: "e.g. the Google account used in Meet",
      adding: "Adding",
      addButton: "Add",
    },
  },
  usrRates: {
    newRate: "New rate",
    deleted: "Rate deleted",
    error: "Error",
    deleteFailed: "Delete failed",
    confirmDelete: "Delete this rate?",
    project: "Project",
    user: "User",
    scopeType: {
      project: "project",
      user: "user",
    },
    columns: {
      scope: "Scope",
      rate: "Rate",
      validFrom: "Valid From",
      validTo: "Valid To",
      actions: "Actions",
    },
    perHour: "{{amount}}/h",
    edit: "Edit rate",
    delete: "Delete rate",
    empty: "No rates yet.",
    updated: "Rate updated",
    created: "Rate created",
    updateFailed: "Update failed",
    createFailed: "Create failed",
    positive: "Enter a positive rate",
    validFromRequired: "Valid-from date is required",
    dialogEdit: "Edit {{type}} rate",
    dialogNew: "New {{type}} rate",
    type: {
      billing: "billing",
      cost: "cost",
    },
    target: "Target",
    ratePerHour: "Rate (per hour)",
    validFromLabel: "Valid from",
    validToLabel: "Valid to",
    optional: "(optional)",
    cancel: "Cancel",
    saving: "Saving",
    save: "Save",
    createRate: "Create rate",
  },
};

export const usersCs: typeof usersEn = {
  usr: {
    list: {
      title: "Uživatelé",
      total_one: "{{count}} uživatel celkem",
      total_other: "Uživatelů celkem: {{count}}",
      total_few: "{{count}} uživatelé celkem",
      search: "Hledat podle jména nebo e-mailu...",
      empty: "Žádní uživatelé nenalezeni.",
      showFormer: "Zobrazit i bývalé",
      former: "bývalý",
      formerSuffix: "(bývalý)",
      viewAsTitle: "Zobrazit Hub jako tento uživatel (jen pro čtení)",
      viewAs: "Zobrazit jako",
      columns: {
        name: "Jméno",
        email: "E-mail",
        role: "Role",
        gitlabId: "GitLab ID",
        iban: "IBAN",
        entries: "Záznamy",
        joined: "Od",
        actions: "Akce",
      },
    },
    roleBadge: {
      admin: "admin",
      accountant: "účetní",
    },
    role: {
      admin: "Admin",
      accountant: "Účetní",
    },
    approval: {
      worked: "odpracováno",
      approved: "schváleno",
      paid: "vyplaceno",
      rejected: "zamítnuto",
    },
    billing: {
      unbilled: "nevyfakturováno",
      to_invoice: "k fakturaci",
      invoiced: "vyfakturováno",
      non_billable: "nefakturovatelné",
    },
    detail: {
      clockify: "Clockify: {{name}}",
      noProject: "Bez projektu",
      overhead: "režie",
      backToUsers: "Zpět na uživatele",
      globalRole: "Globální role",
      discord: "Discord",
      linked: "propojeno",
      notLinked: "nepropojeno",
      editUser: "Upravit uživatele",
      editDescription: "Změna globální role a platebních údajů. Vytvořen <date/>.",
      meetingTitle: "Identita na schůzkách",
      meetingDescription:
        "Zda nahraná schůzka pozná, že tento účastník je tato osoba — a tedy zda jí vůbec navrhne čas. Discord páruje podle ID účtu, které bot zjistí z přezdívky; Google Meet páruje jen podle e-mailu, takže účet v Meetu s jinou adresou než přihlášení do Hubu je třeba zde přiřadit. Obojí si uživatel může spravovat sám ve svém profilu.",
      timeEntries: "Záznamy času",
      timeEntriesCount: "Záznamů: {{count}} (zobrazuje se posledních 50)",
      timeEntriesEmpty: "Zatím žádné záznamy času.",
      costRates: "Nákladové sazby",
      costRatesDescription:
        "Kolik firmu stojí hodina tohoto uživatele. Platí od data — zvýšení je nový řádek s pozdějším „Platí od“, ne úprava. Používá se v přehledech marží; uživatel je nevidí.",
      columns: {
        project: "Projekt",
        workItem: "Tiket",
        hours: "Hodiny",
        date: "Datum",
        approval: "Schválení",
        billing: "Fakturace",
      },
    },
    signIn: {
      sent: "Přihlašovací odkaz odeslán",
      notSent: "Přihlašovací odkaz se nepodařilo odeslat",
      failed: "Odkaz se nepodařilo odeslat",
      send: "Poslat přihlašovací odkaz",
    },
    offboard: {
      title: "Odebrán z projektů: {{projects}}, přeřazených tiketů: {{items}}",
      notRemoved: "Neodebrán z: {{list}}",
      manager: "Nově jste manažerem projektů: {{list}}",
      signedOut: "Odhlášen z relací: {{sessions}}, zneplatněných API tokenů: {{tokens}}",
      failed: "Ukončení spolupráce selhalo",
      confirm:
        "Odebrat uživatele {{name}} ze všech projektů (včetně GitLabu), předat jeho otevřené tikety výchozím osobám projektů, odhlásit ho a vypnout mu účet v Hubu?",
      removing: "Odebírám",
      button: "Odebrat ze všech projektů",
    },
    disableAccess: {
      button: "Zakázat přístup do Hubu",
      confirm:
        "Zakázat uživateli {{name}} přístup do Hubu? Bude odhlášen a znovu se nepřihlásí. Členství v projektech i v GitLabu zůstane beze změny.",
      disabling: "Zakazuji",
      done: "Přístup do Hubu zakázán. Projekty a GitLab beze změny.",
      failed: "Přístup do Hubu se nepodařilo zakázat",
    },
    disabled: {
      badge: "Vypnutý",
      enable: "Zapnout účet",
      enabling: "Zapínám",
      enabled: "Účet zapnut. Projekty a role mu přidejte znovu ručně.",
      failed: "Účet se nepodařilo zapnout",
    },
    edit: {
      updated: "Uživatel upraven",
      error: "Chyba",
      updateFailed: "Úprava selhala",
      globalRole: "Globální role",
      noneRole: "Žádná (jen člen projektu)",
      ibanPlaceholder: "např. CZ6508000000192000145399",
      saving: "Ukládám",
      save: "Uložit změny",
    },
    newClient: {
      created: "Klientský účet vytvořen",
      noInvite: "Přidejte ho k projektu na stránce Členové projektu.",
      invited: "Pozvánka je na cestě. Přidejte ho k projektu na stránce Členové projektu.",
      inviteFailed:
        "Účet existuje, ale pozvánka se neodeslala. {{error}} Pošlete ji znovu ze stránky uživatele, až e-mail zase poběží.",
      trigger: "Nový klient",
      title: "Nový klientský účet",
      intro:
        "Pro klienta bez GitLabu. Přihlašuje se odkazem zaslaným na tento e-mail a vidí jen projekty, kde má roli Klient.",
      name: "Jméno",
      email: "E-mail",
      sendInvite: "Poslat pozvánku hned (odkaz platí 7 dní)",
      cancel: "Zrušit",
      create: "Vytvořit",
    },
    discord: {
      saveFailed: "Přezdívku se nepodařilo uložit.",
      handle: "Discord přezdívka",
      placeholder: "např. vita.mergl",
      linkedState: "Účet propojen — schůzky mohou této osobě navrhovat čas.",
      notLinkedState: "Nepropojeno — ze schůzek na Discordu se nenavrhuje žádný čas.",
      help: "Uloží se a bot ji pak přiřadí ke skutečnému účtu na Discordu. Jen přiřazený účet — ne samotná přezdívka — umožní nahrané schůzce navrhnout váš čas.",
      linking: "Propojuji",
      save: "Uložit přezdívku",
    },
    emails: {
      loadFailed: "Adresy se nepodařilo načíst.",
      addFailed: "Adresu se nepodařilo přidat.",
      removeFailed: "Adresu se nepodařilo odebrat.",
      signIn: "přihlášení",
      remove: "Odebrat",
      add: "Přidat adresu",
      placeholder: "např. účet Google použitý v Meetu",
      adding: "Přidávám",
      addButton: "Přidat",
    },
  },
  usrRates: {
    newRate: "Nová sazba",
    deleted: "Sazba smazána",
    error: "Chyba",
    deleteFailed: "Smazání selhalo",
    confirmDelete: "Smazat tuto sazbu?",
    project: "Projekt",
    user: "Uživatel",
    scopeType: {
      project: "projekt",
      user: "uživatel",
    },
    columns: {
      scope: "Rozsah",
      rate: "Sazba",
      validFrom: "Platí od",
      validTo: "Platí do",
      actions: "Akce",
    },
    perHour: "{{amount}}/h",
    edit: "Upravit sazbu",
    delete: "Smazat sazbu",
    empty: "Zatím žádné sazby.",
    updated: "Sazba upravena",
    created: "Sazba vytvořena",
    updateFailed: "Úprava selhala",
    createFailed: "Vytvoření selhalo",
    positive: "Zadejte kladnou sazbu",
    validFromRequired: "Datum „platí od“ je povinné",
    dialogEdit: "Upravit {{type}}",
    dialogNew: "Nová {{type}}",
    type: {
      billing: "fakturační sazba",
      cost: "nákladová sazba",
    },
    target: "Cíl",
    ratePerHour: "Sazba (za hodinu)",
    validFromLabel: "Platí od",
    validToLabel: "Platí do",
    optional: "(volitelné)",
    cancel: "Zrušit",
    saving: "Ukládám",
    save: "Uložit",
    createRate: "Vytvořit sazbu",
  },
};
